/* WIT collection — desktop Two/Four column toggle.
   Swaps the product grid's grid--N-col-desktop class and remembers the choice. */
(function () {
  const COL_CLASSES = [
    'grid--1-col-desktop',
    'grid--2-col-desktop',
    'grid--3-col-desktop',
    'grid--4-col-desktop',
    'grid--5-col-desktop',
    'grid--6-col-desktop',
  ];

  class WitGridToggle extends HTMLElement {
    constructor() {
      super();
      this.storageKey = 'wit:grid-cols';
      // Drawer copies defer: a click only marks the choice; it's applied to the
      // grid when the user commits (View Products / Close).
      this.deferred = this.hasAttribute('data-defer');
      this.buttons = Array.from(this.querySelectorAll('button[data-cols]'));
      this.buttons.forEach((btn) =>
        btn.addEventListener('click', (e) => {
          // Stop the click bubbling to Dawn's menu-drawer, which otherwise
          // closes the filter drawer on any button click inside it.
          e.stopPropagation();
          if (this.deferred) this.select(btn.dataset.cols);
          else this.apply(btn.dataset.cols, true);
        })
      );
    }

    connectedCallback() {
      // Re-grab the grid each connect (it can be replaced by AJAX filtering).
      this.grid = document.querySelector(this.dataset.grid || '#product-grid');
      if (this.deferred) {
        // Reflect the grid's current columns visually; don't change anything.
        let current = null;
        if (this.grid) {
          const m = this.grid.className.match(/grid--(\d)-col-desktop/);
          if (m) current = m[1];
        }
        if (!current) {
          try { current = window.localStorage.getItem(this.storageKey); } catch (e) { current = null; }
        }
        if (current) this.select(current);
        return;
      }
      let cols = null;
      try {
        cols = window.localStorage.getItem(this.storageKey);
      } catch (e) {
        cols = null;
      }
      if (cols) this.apply(cols, false);
    }

    // Deferred mode: mark a pending choice without touching the grid.
    select(cols) {
      this.pending = cols;
      this.buttons.forEach((btn) =>
        btn.setAttribute('aria-pressed', btn.dataset.cols === cols ? 'true' : 'false')
      );
    }

    // Apply the pending choice (called on commit: View Products / Close).
    commit() {
      if (this.pending) this.apply(this.pending, true);
    }

    apply(cols, persist) {
      if (!this.grid) this.grid = document.querySelector(this.dataset.grid || '#product-grid');
      if (!this.grid || !cols) return;

      COL_CLASSES.forEach((c) => this.grid.classList.remove(c));
      this.grid.classList.add('grid--' + cols + '-col-desktop');

      // Sync ALL grid-toggle instances (toolbar + drawer copy).
      document.querySelectorAll('wit-grid-toggle button[data-cols]').forEach((btn) =>
        btn.setAttribute('aria-pressed', btn.dataset.cols === cols ? 'true' : 'false')
      );

      if (persist) {
        try {
          window.localStorage.setItem(this.storageKey, cols);
        } catch (e) {
          /* ignore */
        }
      }
    }
  }

  if (!customElements.get('wit-grid-toggle')) {
    customElements.define('wit-grid-toggle', WitGridToggle);
  }

  /* Model / Product image toggle — swaps every card's lead image between its
     product shot (data-product-src = the product's first image, which is the
     flat lay) and its model shot (data-model-src = the custom.product_image
     metafield, falling back to the flat lay when that isn't set).

     The card's <img> is server-rendered from the first image with a full
     responsive srcset, so 'product' is the BASE view: switching back to it
     restores that original src/srcset rather than pinning the fixed-width
     swap URL. Defaults to Product on every page load; the choice is kept only
     in memory so it survives filter re-renders within the session. */
  const BASE_VIEW = 'product';
  let imgView = BASE_VIEW;

  class WitImageToggle extends HTMLElement {
    constructor() {
      super();
      this.deferred = this.hasAttribute('data-defer');
      this.buttons = Array.from(this.querySelectorAll('button[data-view]'));
      this.buttons.forEach((btn) =>
        btn.addEventListener('click', (e) => {
          // Stop the click bubbling to Dawn's menu-drawer (which would close the
          // filter drawer on any button click inside it).
          e.stopPropagation();
          if (this.deferred) this.select(btn.dataset.view);
          else this.apply(btn.dataset.view);
        })
      );
    }

    connectedCallback() {
      if (this.deferred) { this.select(imgView); return; }
      this.apply(imgView);
    }

    // Deferred mode: mark a pending choice without changing the images.
    select(view) {
      if (view !== 'model' && view !== 'product') view = BASE_VIEW;
      this.pending = view;
      this.buttons.forEach((btn) =>
        btn.setAttribute('aria-pressed', btn.dataset.view === view ? 'true' : 'false')
      );
    }

    // Apply the pending choice (called on commit: View Products / Close).
    commit() {
      if (this.pending) this.apply(this.pending);
    }

    apply(view) {
      if (view !== 'model' && view !== 'product') view = BASE_VIEW;
      imgView = view;
      const attr = view === 'product' ? 'productSrc' : 'modelSrc';

      document.querySelectorAll('#product-grid [data-wit-card-img]').forEach((img) => {
        // Stash the server-rendered responsive image once per card, so
        // returning to the base view can put the full srcset back. If a swatch
        // hover-preview is showing right now, the live src is that preview —
        // take wit-card.js's stash of the pre-hover image instead, or we'd
        // remember a colourway preview as this card's base forever.
        if (img.dataset.witBaseSrc === undefined) {
          const hovering = img.dataset.witOrigSrc !== undefined;
          img.dataset.witBaseSrc = (hovering ? img.dataset.witOrigSrc : img.getAttribute('src')) || '';
          img.dataset.witBaseSrcset = (hovering ? img.dataset.witOrigSrcset : img.getAttribute('srcset')) || '';
        }

        if (view === BASE_VIEW) {
          if (img.dataset.witBaseSrc) img.src = img.dataset.witBaseSrc;
          if (img.dataset.witBaseSrcset) img.setAttribute('srcset', img.dataset.witBaseSrcset);
          else img.removeAttribute('srcset');
        } else {
          const src = img.dataset[attr];
          if (!src) return;
          img.removeAttribute('srcset');
          img.src = src;
        }

        // Let the swatch hover-preview revert to this new base next time.
        delete img.dataset.witOrigSrc;
        delete img.dataset.witOrigSrcset;
      });

      // Sync ALL image-toggle instances (toolbar + drawer copy).
      document.querySelectorAll('wit-image-toggle button[data-view]').forEach((btn) =>
        btn.setAttribute('aria-pressed', btn.dataset.view === view ? 'true' : 'false')
      );
    }
  }

  if (!customElements.get('wit-image-toggle')) {
    customElements.define('wit-image-toggle', WitImageToggle);
  }

  /* Sort dropdown (grey underlay). Re-renders the grid via the theme's
     FacetFiltersForm, preserving any active filters in the URL. */
  class WitSort extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const toggle = this.querySelector('.wit-sort__toggle');
      if (toggle) {
        toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleAttribute('open');
        });
      }
      this.querySelectorAll('a[data-sort]').forEach((a) => {
        a.addEventListener('click', (e) => {
          e.preventDefault();
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', a.dataset.sort);
          const qs = url.search.slice(1);
          if (window.FacetFiltersForm && typeof window.FacetFiltersForm.renderPage === 'function') {
            window.FacetFiltersForm.renderPage(qs);
          } else {
            window.location.search = qs;
          }
          this.removeAttribute('open');
        });
      });
    }
  }

  if (!customElements.get('wit-sort')) {
    customElements.define('wit-sort', WitSort);
  }

  // Close any open sort dropdown when clicking outside it.
  document.addEventListener('click', (e) => {
    document.querySelectorAll('wit-sort[open]').forEach((el) => {
      if (!el.contains(e.target)) el.removeAttribute('open');
    });
  });

  // Re-apply saved preferences when the grid changes underneath us. Facet AJAX
  // replaces the whole toolbar, so those toggles re-run connectedCallback on
  // their own; these two cases don't, and need the nudge:
  //   - the theme editor reloading the section
  //   - "Load more" appending fresh cards (the collection section dispatches
  //     wit:grid-appended once it has appended them)
  document.addEventListener('shopify:section:load', reapply);
  document.addEventListener('wit:grid-appended', reapply);
  function reapply() {
    document.querySelectorAll('wit-grid-toggle, wit-image-toggle').forEach((el) => {
      if (el && typeof el.connectedCallback === 'function') el.connectedCallback();
    });
  }
})();